import React, { useState } from 'react';

// Specialized Image Component with Robust Fallback
const SportImage = ({ src, size = 24, className = "", alt = "Icon" }) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
        return (
            <div
                className={`${className} flex items-center justify-center bg-white/10 rounded-full`}
                style={{ width: size, height: size, minWidth: size, minHeight: size }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width={Math.max(12, size / 1.5)} height={Math.max(12, size / 1.5)} viewBox="0 0 24 24" fill="#F59E0B">
                    <path d="M12 2l3 9h9l-7 5 3 9-8-6-8 6 3-9-7-5h9z" />
                </svg>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            width={size}
            height={size}
            className={`${className} object-contain drop-shadow-md select-none`}
            style={{ minWidth: size, minHeight: size, display: 'block' }}
            loading="lazy"
            onError={() => setHasError(true)}
        />
    );
};

// --- BASE TWEMOJI URL ---
const TWEMOJI_BASE = "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/svg";

// --- VALIDATED ICONS ---

// 1. SOCCER: Working Wikimedia Image
export const SoccerIcon = (props) => <SportImage src="https://upload.wikimedia.org/wikipedia/commons/d/d3/Soccerball.svg" alt="Fútbol" {...props} />;

// 2. BASKETBALL: Working Wikimedia Image
export const BasketballIcon = (props) => <SportImage src="https://upload.wikimedia.org/wikipedia/commons/7/7a/Basketball.png" alt="Baloncesto" {...props} />;

// 3. TENNIS: Twemoji (Ball)
export const TennisIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3be.svg`} alt="Tenis" {...props} />;

// 4. BASEBALL: Twemoji (Ball)
export const BaseballIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/26be.svg`} alt="Béisbol" {...props} />;

// 5. AMERICAN FOOTBALL: Twemoji (Ball)
export const AmericanFootballIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3c8.svg`} alt="Fútbol Americano" {...props} />;

// 6. ICE HOCKEY: Twemoji (Puck/Stick)
export const IceHockeyIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3d2.svg`} alt="Hockey" {...props} />;

// 7. VOLLEYBALL: Twemoji (Ball)
export const VolleyballIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3d0.svg`} alt="Voleibol" {...props} />;

// 8. HANDBALL: Twemoji (Person Playing Handball) - Best available standard
export const HandBallIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f93e.svg`} alt="Balonmano" {...props} />;

// 9. TABLE TENNIS: Twemoji (Paddle & Ball)
export const TableTennisIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3d3.svg`} alt="Tenis de Mesa" {...props} />;

// 10. RUGBY: Twemoji (Ball)
export const RugbyIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3c9.svg`} alt="Rugby" {...props} />;

// 11. BOXING: Twemoji (Glove)
export const BoxingIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f94a.svg`} alt="Boxeo" {...props} />;
export const MMAIcon = BoxingIcon;

// 12. ESPORTS: Twemoji (Controller)
export const EsportsIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3ae.svg`} alt="Esports" {...props} />;

// 13. MOTORSPORT: Twemoji (Race Car)
export const MotorsportIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3ce.svg`} alt="Motor" {...props} />;

// 14. CRICKET: Twemoji (Bat & Ball)
export const CricketIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3cf.svg`} alt="Criquet" {...props} />;

// 15. BEACH VOLLEY: Twemoji (Volleyball)
export const BeachVolleyIcon = VolleyballIcon;

// 16. FUTSAL: Twemoji (Soccer Ball)
export const FutsalIcon = SoccerIcon;

// 17. ARCHERY: Twemoji (Bullseye)
export const ArcheryIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3af.svg`} alt="Tiro con Arco" {...props} />;

// 18. BANDY: Twemoji (Field Hockey Stick & Ball - closest)
export const BandyIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3d1.svg`} alt="Bandy" {...props} />;

// 19. ALPINE SKIING: Twemoji (Skis)
export const AlpineSkiingIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3bf.svg`} alt="Esquí Alpino" {...props} />;

// 20. ATHLETICS: Twemoji (Runner)
export const AthleticsIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3c3.svg`} alt="Atletismo" {...props} />;

// 21. ARTISTIC SWIMMING: Twemoji (Person Swimming)
export const ArtisticSwimmingIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3ca.svg`} alt="Natación" {...props} />;

// 22. AQUATICS/WATER POLO: Twemoji (Person Playing Water Polo)
export const Waves = (props) => <SportImage src={`${TWEMOJI_BASE}/1f93d.svg`} alt="Waterpolo" {...props} />;

// 23. BADMINTON: Twemoji (Racquet & Shuttlecock)
export const BadmintonIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3f8.svg`} alt="Bádminton" {...props} />;

// 24. CYCLING: Twemoji (Bicycle)
export const CyclingIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f6b4.svg`} alt="Ciclismo" {...props} />;

// 25. OTHERS
export const DartsIcon = ArcheryIcon; // Use Bullseye
export const AirRacingIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/2708.svg`} alt="Air Racing" {...props} />; // Airplane
export const AussieRulesIcon = RugbyIcon;
export const Flame = (props) => <SportImage src={`${TWEMOJI_BASE}/1f525.svg`} alt="Otros" {...props} />;
export const GenericTrophyIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f3c6.svg`} alt="Trofeo" {...props} />;

// 26. CANICAS (Marbles) / 7BallRun
export const SevenBallRunIcon = (props) => <SportImage src={`${TWEMOJI_BASE}/1f52e.svg`} alt="Canicas" {...props} />; // Crystal Ball / Marble
